import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocal } from "hooks/useLocal";
import licensedby from "../../assets/Licensedby/logo.png";
import licensedby1 from "../../assets/Licensedby/logo-1.png";
import licensedby2 from "../../assets/Licensedby/logo-2.png";
import licensedby3 from "../../assets/Licensedby/logo-3.png";
import licensedby4 from "../../assets/Licensedby/logo-4.png";

const logos = [licensedby, licensedby1, licensedby2, licensedby3, licensedby4];
const carouselLogos = [...logos, ...logos];

function useViewportWidth() {
  const [width, setWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 0,
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  return width;
}

// Logos fit comfortably at ~180px each + gaps, so 5 logos need ~1000px
const CAROUSEL_BREAKPOINT = 1024;

export function LicensedBy() {
  const { t } = useTranslation(["common"]);
  const { direction, isArabic } = useLocal();
  const dirAttr: string | undefined = Array.isArray(direction)
    ? direction[0]
    : direction;

  const isRTL = dirAttr === "rtl";
  const viewportWidth = useViewportWidth();
  const isMobile = viewportWidth < 768;
  const isTablet = viewportWidth < CAROUSEL_BREAKPOINT;

  // Cards per view: mobile shows 2.5, tablet shows 3.5, desktop shows 5
  const cardsPerView = isMobile ? 2.5 : isTablet ? 3.5 : 5;

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: true,
      align: "start",
      slidesToScroll: 1,
      direction: isRTL ? "rtl" : "ltr",
      active: true,
    },
    [Autoplay({ delay: 3500, stopOnInteraction: false, playOnInit: true })],
  );

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    setScrollSnaps(emblaApi.scrollSnapList());
    emblaApi.on("select", onSelect);
    onSelect();
    return () => {
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi, onSelect]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.reInit({
      direction: isRTL ? "rtl" : "ltr",
      active: true,
    });
  }, [isRTL, emblaApi]);

  const scrollTo = useCallback(
    (index: number) => emblaApi && emblaApi.scrollTo(index),
    [emblaApi],
  );

  return (
    <section
      dir={dirAttr}
      className={`bg-[#ffffff] pb-10 ${isArabic ? "font-ibmplex" : "font-inter"}`}
    >
      <article className="py-10 px-6 max-w-[1400px] mx-auto">
        {/* Title */}
        <p
          className={`text-center text-2xl md:text-[32px] font-bold text-gray mb-20 ${isArabic ? "font-ibmplex" : "font-funnel"}`}
        >
          {t("ourExpertsAreLicensedBy")}
        </p>

        {/* Carousel */}
        <div
          className="overflow-hidden"
          ref={emblaRef}
          dir={isRTL ? "rtl" : "ltr"}
        >
          <div
            className="flex items-center"
            style={{
              marginLeft: isRTL ? "0" : "-16px",
              marginRight: isRTL ? "-16px" : "0",
            }}
          >
            {carouselLogos.map((logo, i) => (
              <div
                key={i}
                className="flex-none flex items-center justify-center"
                style={{
                  width: `calc(100% / ${cardsPerView})`,
                  paddingLeft: isRTL ? "0" : "16px",
                  paddingRight: isRTL ? "16px" : "0",
                }}
              >
                <img
                  src={logo}
                  alt={`Licensed by logo ${i + 1}`}
                  className="h-12 object-contain w-full"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Dots — only on mobile */}
        {/* {isMobile && scrollSnaps?.length > 0 && (
          <div className="flex justify-center items-center gap-2 mt-6">
            {scrollSnaps.map((_, index) => (
              <button
                key={index}
                onClick={() => scrollTo(index)}
                aria-label={`Go to slide ${index + 1}`}
                className={`rounded-full transition-all duration-300 ${
                  index === selectedIndex
                    ? "bg-[#000] w-3 h-3"
                    : "bg-gray-300 w-2.5 h-2.5"
                }`}
              />
            ))}
          </div>
        )} */}
      </article>
    </section>
  );
}
